import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { FREQUENCY_OPTIONS } from "@/components/NewsletterForm";
import { markWelcomeUsed } from "@/lib/referral";

function ChequeConfirmation({ order, formatAmount }) {
  const ref = order.session_id.slice(-8).toUpperCase();
  return (
    <div data-testid="cheque-confirmed" className="space-y-6">
      <CheckCircle2 size={56} className="mx-auto text-sage" />
      <h1 className="font-serif text-4xl font-semibold text-bark">Merci pour votre commande !</h1>
      <p data-testid="cheque-confirmed-message" className="text-base font-semibold text-bark">{order.cheque.message}</p>
      <div className="mx-auto max-w-md space-y-2 rounded-2xl border border-amber-800/15 bg-white p-6 text-left text-sm text-bark-soft">
        <p>Commande n° <strong data-testid="cheque-order-ref" className="text-bark">{ref}</strong></p>
        <p>Montant du chèque : <strong data-testid="cheque-amount" className="text-amber-deep">{formatAmount(order.amount, order.currency)}</strong></p>
        <p>À l'ordre de : <strong className="text-bark">{order.cheque.payee}</strong></p>
        <p>À envoyer à :<br /><span data-testid="cheque-address" className="whitespace-pre-line font-medium text-bark">{order.cheque.address}</span></p>
        <p className="text-xs">Merci d'indiquer le n° {ref} au dos du chèque. Ces informations vous sont aussi envoyées par e-mail.</p>
      </div>
    </div>
  );
}

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = (searchParams.get("session_id") || searchParams.get("token"));
  const [state, setState] = useState("verifying");
  const [order, setOrder] = useState(null);
  const { clearCart } = useCart();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("timeout");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "awaiting_cheque") {
          if (!cancelled) {
            const { data: cs } = await api.get("/payments/cheque-settings");
            setOrder({ ...data, cheque: cs });
            setState("cheque");
            clearCart();
            markWelcomeUsed();
          }
          return;
        }
        if (data.payment_status === "paid") {
          if (!cancelled) {
            setOrder(data);
            setState("paid");
            clearCart();
            markWelcomeUsed();
          }
          return;
        }
      } catch {
        // on réessaie
      }
      attempts.current += 1;
      if (attempts.current < 15) {
        setTimeout(poll, 2000);
      } else if (!cancelled) {
        setState("timeout");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const formatAmount = (amount, currency) =>
    new Intl.NumberFormat("fr-FR", { style: "currency", currency: (currency || "eur").toUpperCase() }).format(amount || 0);

  // Proposition d'inscription à la newsletter après commande (si non abonné)
  const [nlEmail, setNlEmail] = useState("");
  const [nlFrequency, setNlFrequency] = useState("monthly");
  const [nlState, setNlState] = useState("idle"); // idle | hidden | loading | done

  useEffect(() => {
    if (state !== "paid" || !order) return;
    const email = order.customer_email || "";
    setNlEmail(email);
    if (email) {
      api.get("/newsletter/status", { params: { email } })
        .then(({ data }) => { if (data.subscribed) setNlState("hidden"); })
        .catch(() => {});
    }
  }, [state, order]);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!nlEmail.trim()) return;
    setNlState("loading");
    try {
      const { data } = await api.post("/newsletter", { email: nlEmail.trim(), frequency: nlFrequency });
      if (data.status === "already") {
        setNlState("hidden");
      } else {
        toast.success(data.message);
        setNlState("done");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Inscription impossible pour le moment.");
      setNlState("idle");
    }
  };

  return (
    <div data-testid="payment-success-page" className="mx-auto max-w-2xl px-4 py-20 text-center sm:px-8">
      {state === "verifying" && (
        <div data-testid="payment-verifying" className="flex flex-col items-center gap-4">
          <Loader2 size={40} className="animate-spin text-amber-deep" />
          <h1 className="font-serif text-3xl font-semibold text-bark">Nous vérifions votre paiement…</h1>
          <p className="text-sm text-bark-soft">Quelques secondes suffisent, le temps que les abeilles fassent le trajet.</p>
        </div>
      )}

      {state === "cheque" && order && <ChequeConfirmation order={order} formatAmount={formatAmount} />}

      {state === "paid" && order && (
        <div data-testid="payment-confirmed" className="space-y-6">
          <CheckCircle2 size={56} className="mx-auto text-sage" />
          <h1 className="font-serif text-4xl font-semibold text-bark">Merci pour votre commande !</h1>
          <p className="text-base text-bark-soft">
            Votre paiement de <strong className="text-amber-deep">{formatAmount(order.amount, order.currency)}</strong> est confirmé.
            Vous recevrez un e-mail de confirmation, et vos douceurs partiront du rucher sous 48 h.
          </p>
          {order.items?.length > 0 && (
            <ul data-testid="order-summary" className="mx-auto max-w-md space-y-2 rounded-2xl border border-amber-800/15 bg-white p-6 text-left text-sm">
              {order.items.map((item, idx) => (
                <li key={item.box_label || `${item.product_id}-${idx}`} className="flex justify-between gap-4">
                  <span className="text-bark">
                    {item.quantity} × {item.name}
                    {item.box_label && <span className="block text-xs font-medium text-amber-deep">{item.box_label}</span>}
                    {item.subscription && <span className="block text-xs font-medium text-sage">Abonnement {item.subscription.cadence === "monthly" ? "mensuel" : "bimestriel"} · {item.subscription.deliveries} livraisons/an</span>}
                    {item.gift && item.gift.is_gift && <span className="block text-xs font-medium text-terra">🎁 Livré à {item.gift.recipient_name}</span>}
                  </span>
                  <span className="font-semibold text-amber-deep">{formatAmount((item.unit_amount_cents * item.quantity) / 100)}</span>
                </li>
              ))}
              {order.shipping && (
                <li className="flex justify-between gap-4 text-stone-mute">
                  <span>Livraison — {order.shipping.name}</span>
                  <span>{order.shipping.charge_cents > 0 ? formatAmount(order.shipping.charge_cents / 100) : "Offerte"}</span>
                </li>
              )}
            </ul>
          )}
          {nlState !== "hidden" && nlState !== "done" && (
            <form
              data-testid="success-newsletter-card"
              onSubmit={handleNewsletter}
              className="mx-auto max-w-md space-y-4 rounded-2xl border border-honey/40 bg-amber-50/60 p-6 text-left"
            >
              <p className="flex items-center gap-2 font-serif text-xl font-semibold text-bark">
                <Mail size={18} className="text-amber-deep" /> Restez au parfum des récoltes
              </p>
              <p className="text-sm text-bark-soft">
                Recevez la Gazette Champêtre : nouvelles de la ruche, recettes et récoltes en avant-première.
              </p>
              <input
                data-testid="success-newsletter-email"
                type="email"
                required
                value={nlEmail}
                onChange={(e) => setNlEmail(e.target.value)}
                placeholder="Votre adresse e-mail"
                className="w-full rounded-full border border-amber-900/20 bg-white px-5 py-3 text-sm outline-none transition-colors focus:border-amber-deep"
              />
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-stone-mute">Recevoir la Gazette :</span>
                {FREQUENCY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`success-newsletter-frequency-${opt.value}`}
                    onClick={() => setNlFrequency(opt.value)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-all duration-200 ${
                      nlFrequency === opt.value
                        ? "bg-amber-deep text-white shadow-sm"
                        : "border border-amber-900/20 text-bark-soft hover:border-amber-deep hover:text-amber-deep"
                    }`}
                  >
                    {opt.short}
                  </button>
                ))}
              </div>
              <button
                data-testid="success-newsletter-submit"
                type="submit"
                disabled={nlState === "loading"}
                className="w-full rounded-full bg-amber-deep py-3 text-sm font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-[0.98] disabled:opacity-60"
              >
                {nlState === "loading" ? "Inscription…" : "S'abonner à la Gazette"}
              </button>
            </form>
          )}
          {nlState === "done" && (
            <p data-testid="success-newsletter-done" className="mx-auto max-w-md rounded-2xl border border-sage/30 bg-sage-light px-6 py-4 text-sm font-medium text-sage">
              Inscription confirmée — la Gazette Champêtre arrive bientôt dans votre boîte !
            </p>
          )}
          <Link to="/boutique" data-testid="success-back-to-shop" className="inline-flex rounded-full bg-amber-deep px-7 py-3.5 font-medium text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95">
            Retour à la boutique
          </Link>
        </div>
      )}

      {state === "timeout" && (
        <div data-testid="payment-pending" className="space-y-6">
          <Loader2 size={48} className="mx-auto text-honey" />
          <h1 className="font-serif text-3xl font-semibold text-bark">Confirmation en cours…</h1>
          <p className="text-base text-bark-soft">
            La confirmation de paiement prend plus de temps que prévu. Pas d'inquiétude : si votre paiement a abouti,
            votre commande est bien enregistrée et vous recevrez un e-mail de confirmation.
          </p>
          <Link to="/" data-testid="pending-back-home" className="inline-flex rounded-full border-2 border-amber-700/30 px-7 py-3.5 font-medium text-amber-900 transition-all duration-200 hover:border-amber-deep hover:bg-amber-50">
            Retour à l'accueil
          </Link>
        </div>
      )}
    </div>
  );
}
